import { Button } from '@/components/base/button'
import { Dialog, DialogActions, DialogBody, DialogTitle, ShowProps } from '@/components/base/dialog'
import { Divider } from '@/components/base/divider'
import { useEffect, useState } from 'react'
import { VenueQuery } from './__generated__/show_query'
import { Query } from './show_query'
import { useQuery } from '@apollo/client'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Subheading } from '@/components/base/heading'
import { MapPinIcon } from '@heroicons/react/24/solid'
import { Accordion } from '@/components/accordion'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from '@/components/venue/delete_mutation'
import { Query as TableQuery } from '@/components/event/table_query'
import { EditDialog } from './edit_dialog'
import { AttachmentResourceType, CustomFieldResource, DocumentSubjectType } from '@/types'
import { Table as EventTable } from '@/components/event/table'
import { Table as ContactTable } from '@/components/venue_contact/table'
import { Table as CustomFieldValueTable } from '@/components/custom_field_value/table'
import { Table as DocumentsTable } from '@/components/document/table'
import { Collection as AttachmentsCollection } from '@/components/attachment/collection'

type Venue = VenueQuery['venues']['nodes'][0]

export const ShowDialog = ({ id, open, setOpen }: ShowProps) => {
  const [openEdit, setOpenEdit] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [venue, setVenue] = useState<Venue | null>(null)

  const { data, loading, error } = useQuery<VenueQuery>(Query, { variables: { filters: { idEq: id } } })

  useEffect(() => {
    if (!data || !id) { return }

    setVenue(data.venues.nodes[0])
  }, [id, data])

  const onEdit = () => {
    setOpenEdit(true)
  }
  const onDelete = () => {
    setOpenDelete(true)
  }

  const onDeleteSuccess = () => {
    setOpenDelete(false)
    setOpen(false)
  }

  return (
    <>
      <Dialog open={open} size="7xl" onClose={setOpen}>
        <DialogTitle>
          { loading || !venue
            ? <Loading />
            : <div className="flex flex-row justify-between">
              <div>
                <span className="flex flex-row gap-4 items-center pb-6">
                  { venue.name }
                </span>
                <span className="flex flex-row gap-2 items-center">
                  <MapPinIcon className="h-6 w-6" />
                  <Subheading>{ venue.address }</Subheading>
                </span>
              </div>
            </div> }
        </DialogTitle>
        <DialogBody>
          { error && <Error message={error.message} />}
          { venue && <div className="flex flex-col gap-4">
            { venue.map &&
              <img className="max-w-3xl self-center rounded-md" src={venue.map.url} />}
            <Divider />
            { venue.notes &&
              <Accordion title="Notes">
                {venue.notes}
              </Accordion>}
            <Accordion title="Contacts">
              <ContactTable pageSize={20} venueId={venue.id} fixedHeight />
            </Accordion>
            <Accordion title="Custom Fields">
              <CustomFieldValueTable
                filters={{ resourceIdEq: venue.id, resourceTypeEq: CustomFieldResource.Venue }}
                pageSize={20}
                fixedHeight
              />
            </Accordion>
            <Accordion title="Events">
              <EventTable filters={ { venueIdEq: venue.id } } pageSize={20} fixedHeight />
            </Accordion>
            <Accordion title="Attachments">
              <AttachmentsCollection
                filters={{ nameEq: 'attachments', resourceIdEq: venue.id, resourceTypeEq: AttachmentResourceType.Venue }}
                pageSize={10}
                fixedHeight
              />
            </Accordion>
            <Accordion title="Documents">
              <DocumentsTable filters={{ subjectIdEq: venue.id, subjectTypeEq: DocumentSubjectType.Venue }} pageSize={20} fixedHeight />
            </Accordion>
          </div> }
        </DialogBody>
        <DialogActions>
          { venue && <Button color="amber" onClick={onEdit}>Edit</Button> }
          { venue && <Button color="red" onClick={onDelete}>Delete</Button> }
        </DialogActions>
      </Dialog>
      { venue && <EditDialog open={openEdit} setOpen={setOpenEdit} venue={venue} /> }
      {
        venue &&
          <DeleteDialog
            id={venue.id}
            mutation={DeleteMutation}
            open={openDelete}
            refetchQueries={[TableQuery]}
            setOpen={setOpenDelete}
            onSuccess={onDeleteSuccess}
          />
      }
    </>
  )
}
